import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription, combineLatest } from 'rxjs';
import { SelectModule } from 'primeng/select';

import { DataService } from '../../services/data';
import { LanguageService } from '../../services/language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-language-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, TranslateModule],
  template: `
    @if (habilitar) {
      <p-select
        [options]="options"
        [ngModel]="currentLang"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        (ngModelChange)="onChange($event)"
        styleClass="lang-select">
      </p-select>
    }
  `,
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  habilitar = false;
  currentLang = 'es';
  options: Array<{ label: string; value: 'es' | 'it' }> = [
    { label: 'Español', value: 'es' },
    { label: 'Italiano', value: 'it' },
  ];

  private sub = new Subscription();

  constructor(
    private data: DataService,
    private lang: LanguageService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.currentLang = this.lang.getCurrentLang();
    this.rebuildLabels();

    this.sub.add(
      combineLatest([
        this.data.habilitarSelectorIdioma$,
        this.data.idiomasDisponibles$
      ]).subscribe(
        ([habilitar, disponibles]) => {
          this.habilitar = habilitar;
          const set = new Set(disponibles);
          this.options = [
            ...(set.has('es') ? [{ label: this.translate.instant('COMMON.LANG_ES'), value: 'es' as const }] : []),
            ...(set.has('it') ? [{ label: this.translate.instant('COMMON.LANG_IT'), value: 'it' as const }] : []),
          ];

          // Si selector está deshabilitado, solo ocultar (DataService fuerza UI a ES sin persistir).
          // CRÍTICO: no llamar onChange('es') aquí, porque este observable emite primero con el default (false)
          // antes de que llegue el snapshot real de Settings/features y se terminaría pisando el idioma guardado.
          if (!habilitar) return;

          // Si el idioma actual no está disponible, forzar fallback.
          if (!set.has(this.currentLang as any)) {
            const fallback = set.has('es') ? 'es' : (this.options[0]?.value ?? 'es');
            this.onChange(fallback);
          }
        }
      )
    );

    // Mantener currentLang en sync y refrescar labels al cambiar idioma
    this.sub.add(
      this.lang.currentLang$.subscribe((l) => {
        this.currentLang = l;
        this.rebuildLabels();
      })
    );
  }

  private rebuildLabels(): void {
    // reconstruye labels por si cambia el idioma (ngx-translate)
    this.options = this.options.map((o) => ({
      ...o,
      label: o.value === 'it'
        ? this.translate.instant('COMMON.LANG_IT')
        : this.translate.instant('COMMON.LANG_ES'),
    }));
  }

  onChange(value: 'es' | 'it'): void {
    if (this.currentLang === value) return;
    // No depender de options/idiomasDisponibles$ para persistir la preferencia del usuario.
    // DataService ya se encarga de forzar ES (sin persistir) si la misión deshabilita selector,
    // o de validar el idioma contra idiomasDisponibles cuando está habilitado.
    this.lang.setLanguage(value, ['es', 'it']);
    this.currentLang = this.lang.getCurrentLang();
    this.rebuildLabels();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}

